import type { ThreeEvent } from "@react-three/fiber";
import { Tree } from "fluid-framework";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Group } from "three";

import {
  getRoom,
  isUserInRoom,
  userMoveToRoom,
} from "../features/fluid/fluid.service.js";
import { useFluid } from "../features/fluid/hooks/use-fluid.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";
import { useHover } from "./use-hover.js";

export function useRoomInteraction({ roomId }: { roomId: string }) {
  const { fluidUserId, sharedTree } = useFluid();
  const [shouldHover, setShouldHover] = useState(true);
  const groupRef = useRef<Group>(null);
  const hovered = useHover(groupRef, shouldHover);

  const updateHoverState = useCallback(() => {
    setShouldHover(!isUserInRoom({ roomId, sharedTree, userId: fluidUserId }));
  }, [fluidUserId, sharedTree, roomId]);

  useEffect(() => {
    updateHoverState();
    const room = getRoom({ roomId, sharedTree });

    if (!room) {
      console.warn(`Room ${roomId} not found in shared tree`);
      return;
    }

    const unsubscribe = Tree.on(room, "treeChanged", () => {
      updateHoverState();
    });

    return () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [sharedTree, roomId, updateHoverState]);

  const handleRoomClick = useCallback(
    (event: ThreeEvent<MouseEvent>): boolean => {
      event.stopPropagation();

      if (!roomId) {
        console.error("Cannot join room - missing required data");
        return false;
      }

      try {
        if (!getRoom({ roomId, sharedTree })) {
          return false;
        }

        if (isUserInRoom({ roomId, sharedTree, userId: fluidUserId })) {
          return false;
        }

        userMoveToRoom({ roomId, sharedTree, userId: fluidUserId });
        return true;
      } catch (error) {
        recordErrorInSentry(error);
        console.error(`Error joining room ${roomId}:`, error);
        return false;
      }
    },
    [fluidUserId, sharedTree, roomId]
  );

  return {
    groupRef,
    handleRoomClick,
    hovered,
  };
}
